/**
 * ============================================================
 * KEJJA OS — src/pages/community/CommunityGoalsPage.tsx
 * Shared savings goals with contributions + Finance linking.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import {
  Target, Plus, X, Link as LinkIcon, Trash2, Check,
  TrendingUp, Calendar, ChevronRight,
} from 'lucide-react';

import { useCommunityDetail } from './CommunityDetailLayout';
import {
  useGoals, useCreateGoal, useDeleteGoal, useUpdateGoal,
  useContributions, useCreateContribution, useDeleteContribution,
  useLinkableTransactions,
} from '@/hooks/useCommunity';
import type { GoalWithProgress, CommunityContribution } from '@/lib/communityApi';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmojiPicker } from '@/components/EmojiPicker';
import { cn } from '@/lib/utils';

function fmtKES(minor: number): string {
  const major = minor / 100;
  return 'KES ' + major.toLocaleString('en-KE', { maximumFractionDigits: 0 });
}

export default function CommunityGoalsPage() {
  const { homeId, community, role } = useCommunityDetail();
  const { data, isLoading } = useGoals(homeId, community.id);
  const [showForm, setShowForm] = useState(false);
  const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);

  const goals = data?.goals ?? [];
  const isAdmin = role === 'admin';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {goals.length} goal{goals.length === 1 ? '' : 's'}
          {' · '}
          <span className="text-emerald-600">{goals.filter((g) => g.isComplete).length} done</span>
        </div>
        <Button
          size="sm"
          className="bg-rose-600 hover:bg-rose-700 text-white"
          onClick={() => setShowForm(true)}
        >
          <Plus className="w-4 h-4 mr-1" /> Goal
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
      ) : goals.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Target className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No shared goals yet. Set one up.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {goals.map((g) => (
            <GoalRow
              key={g.id}
              goal={g}
              expanded={expandedGoalId === g.id}
              onToggle={() => setExpandedGoalId(expandedGoalId === g.id ? null : g.id)}
              canManage={isAdmin || g.createdByHomeId === homeId}
              homeId={homeId}
              communityId={community.id}
            />
          ))}
        </div>
      )}

      {showForm && (
        <GoalForm
          homeId={homeId} communityId={community.id}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// GoalRow — with expand to show contributions
// ============================================================

function GoalRow({
  goal, expanded, onToggle, canManage, homeId, communityId,
}: {
  goal: GoalWithProgress;
  expanded: boolean;
  onToggle: () => void;
  canManage: boolean;
  homeId: string;
  communityId: string;
}) {
  const deleteMut = useDeleteGoal(homeId, communityId);
  const updateMut = useUpdateGoal(homeId, communityId);
  const [showContribForm, setShowContribForm] = useState(false);

  const pctBg = goal.isComplete ? 'bg-emerald-600' : 'bg-rose-600';
  const displayPercent = Math.min(100, goal.percentComplete);

  return (
    <Card className={cn(goal.isComplete && 'bg-emerald-50/40 dark:bg-emerald-950/10')}>
      <CardContent className="p-3">
        <button onClick={onToggle} className="w-full text-left">
          <div className="flex items-start gap-2.5">
            <div className="h-10 w-10 rounded-lg bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 flex items-center justify-center flex-shrink-0 text-lg">
              {goal.icon ?? <Target className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm truncate flex-1">{goal.name}</span>
                {goal.isComplete && <Check className="w-3.5 h-3.5 text-emerald-600" />}
              </div>
              <div className="mt-1 flex items-baseline gap-1.5 text-[11px]">
                <span className="font-semibold text-foreground tabular-nums">
                  {fmtKES(goal.contributedMinor)}
                </span>
                <span className="text-muted-foreground">of {fmtKES(goal.targetAmountMinor)}</span>
                <span className="text-muted-foreground ml-auto tabular-nums font-semibold">{displayPercent}%</span>
              </div>
              {/* Progress bar */}
              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all', pctBg)}
                     style={{ width: `${displayPercent}%` }} />
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                <span>{goal.contributorCount} contributor{goal.contributorCount === 1 ? '' : 's'}</span>
                {goal.targetDate && (
                  <span className="flex items-center gap-0.5">
                    <Calendar className="w-2.5 h-2.5" />
                    by {new Date(goal.targetDate).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}
                  </span>
                )}
                <ChevronRight className={cn('w-3 h-3 ml-auto transition-transform', expanded && 'rotate-90')} />
              </div>
            </div>
          </div>
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div className="flex gap-2">
              <Button
                size="sm" className="bg-rose-600 hover:bg-rose-700 text-white flex-1 text-xs"
                onClick={() => setShowContribForm(true)}
              >
                <TrendingUp className="w-3 h-3 mr-1" /> Log contribution
              </Button>
              {canManage && !goal.isComplete && (
                <Button
                  size="sm" variant="outline" className="text-xs"
                  onClick={() => updateMut.mutate({ goalId: goal.id, patch: { isComplete: true } })}
                >
                  Mark complete
                </Button>
              )}
              {canManage && (
                <Button
                  size="sm" variant="ghost"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={() => {
                    if (confirm(`Delete goal "${goal.name}"? All contributions will be unlinked but kept.`)) {
                      deleteMut.mutate(goal.id);
                    }
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>

            <ContributionsList homeId={homeId} communityId={communityId} goalId={goal.id} />
          </div>
        )}

        {showContribForm && (
          <ContributionForm
            homeId={homeId} communityId={communityId} goalId={goal.id}
            onClose={() => setShowContribForm(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// ContributionsList
// ============================================================

function ContributionsList({ homeId, communityId, goalId }: {
  homeId: string; communityId: string; goalId: string;
}) {
  const { data, isLoading } = useContributions(homeId, communityId, goalId);
  const deleteMut = useDeleteContribution(homeId, communityId);

  if (isLoading) return <Skeleton className="h-12" />;
  const contribs = data?.contributions ?? [];

  if (contribs.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground italic text-center py-2">
        No contributions yet. Be the first.
      </p>
    );
  }

  return (
    <div className="space-y-1 max-h-48 overflow-y-auto">
      {contribs.map((c) => (
        <ContributionRow
          key={c.id}
          contribution={c}
          onDelete={() => {
            if (confirm('Delete this contribution?')) deleteMut.mutate(c.id);
          }}
        />
      ))}
    </div>
  );
}

function ContributionRow({ contribution, onDelete }: {
  contribution: CommunityContribution;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs p-2 rounded-md bg-muted/30">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold tabular-nums">{fmtKES(contribution.amountMinor)}</span>
          {contribution.linkedTransactionId && (
            <span className="text-[10px] text-emerald-600 flex items-center gap-0.5">
              <LinkIcon className="w-2.5 h-2.5" /> Finance
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {new Date(contribution.contributedOn).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}
          {contribution.note && <span className="ml-2 truncate">· {contribution.note}</span>}
        </div>
      </div>
      <button onClick={onDelete} className="text-muted-foreground hover:text-rose-600">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ============================================================
// GoalForm
// ============================================================

function GoalForm({ homeId, communityId, onClose }: {
  homeId: string; communityId: string; onClose: () => void;
}) {
  const createMut = useCreateGoal(homeId, communityId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Name is required.'); return; }
    const amt = parseFloat(targetAmount.replace(/[,\s]/g, ''));
    if (!amt || amt <= 0) { setErr('Enter a valid target amount.'); return; }

    createMut.mutate({
      name: name.trim(),
      description: description.trim() || null,
      icon: icon.trim() || null,
      targetAmountMinor: Math.round(amt * 100),
      targetDate: targetDate || null,
    }, {
      onSuccess: () => onClose(),
      onError: (e) => setErr(e.message ?? 'Could not create'),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-md my-8">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">New goal</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  autoFocus value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Group trip to Maasai Mara"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Icon</Label>
                <div className="mt-1 w-16">
                  <EmojiPicker value={icon} onChange={setIcon} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Target (KES)</Label>
                <Input
                  type="text" inputMode="numeric"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  placeholder="50000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Target date (optional)</Label>
                <Input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Description (optional)</Label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full mt-1 min-h-[60px] rounded-md border border-border bg-background p-2 text-xs"
              />
            </div>

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <Button
              type="submit"
              className="bg-rose-600 hover:bg-rose-700 text-white w-full"
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create goal'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// ContributionForm with Finance linking
// ============================================================

function ContributionForm({
  homeId, communityId, goalId, onClose,
}: {
  homeId: string; communityId: string; goalId: string; onClose: () => void;
}) {
  const createMut = useCreateContribution(homeId, communityId);
  const { data: linkableData } = useLinkableTransactions(homeId, 30);

  const [amount, setAmount] = useState('');
  const [contributedOn, setContributedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [linkedTxnId, setLinkedTxnId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  const transactions = linkableData?.transactions ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const amt = parseFloat(amount.replace(/[,\s]/g, ''));
    if (!amt || amt <= 0) { setErr('Enter a valid amount.'); return; }

    createMut.mutate({
      goalId,
      amountMinor: Math.round(amt * 100),
      contributedOn,
      note: note.trim() || null,
      linkedTransactionId: linkedTxnId || null,
    }, {
      onSuccess: () => onClose(),
      onError: (e) => setErr(e.message ?? 'Could not log'),
    });
  };

  // When user picks a transaction, auto-fill amount + date
  const pickTransaction = (txnId: string) => {
    setLinkedTxnId(txnId);
    const t = transactions.find((tx) => tx.id === txnId);
    if (t) {
      setAmount((t.amountMinor / 100).toString());
      setContributedOn(new Date(t.occurredAt).toISOString().slice(0, 10));
      if (t.merchant || t.description) {
        setNote(t.merchant || t.description || '');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-md my-8">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold">Log contribution</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
            </div>

            {transactions.length > 0 && (
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" />
                  Link to Finance transaction (optional)
                </Label>
                <select
                  value={linkedTxnId}
                  onChange={(e) => pickTransaction(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-xs"
                >
                  <option value="">— don't link —</option>
                  {transactions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {new Date(t.occurredAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })}
                      {' · '}KES {(t.amountMinor / 100).toLocaleString()}
                      {t.merchant && ` · ${t.merchant}`}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Picks from your last 30 days of outgoing M-Pesa. Linking auto-fills amount + date.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Amount (KES)</Label>
                <Input
                  type="text" inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="1000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date" value={contributedOn}
                  onChange={(e) => setContributedOn(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. October contribution"
                className="mt-1"
              />
            </div>

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <Button
              type="submit"
              className="bg-rose-600 hover:bg-rose-700 text-white w-full"
              disabled={createMut.isPending}
            >
              {createMut.isPending ? 'Saving…' : 'Log contribution'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
